/**
 * Eine Zeile der gruppierten Sekundärbrennstoff-Tabelle (Phase 34, Weg B). Ausgelagert aus
 * `BrennstoffeSection.tsx` (Vorbild `OtherFuelRow`, Zeile 354-463 vor dem Umbau), damit Plan
 * 34-12 die Sektion umbauen kann, während diese Zeile — samt Menge, Zahlenfeldern, abgeleiteten
 * Anzeigezellen und Aktionsspalte — bereits fertig und geprüft steht.
 *
 * Memoisiert wie ihr Vorbild: ohne `memo()` wurden rund 576 Renders je Dirty-Flip gemessen
 * (`BrennstoffeSection.tsx`, Kommentar über den bestehenden Zeilenkomponenten). `flatIndex` ist
 * die Position der Zeile im FLACHEN Formular-Array `alternativeFuels`, nicht die Position
 * innerhalb ihrer Gruppe — daran hängen die Feldpfade `alternativeFuels.<flatIndex>.<feld>`
 * (siehe `alternativeFuelRows.ts`, Docblock zu `AlternativeFuelGroup`).
 *
 * Fasst den Formularzustand selbst NICHT an: `onRemove`/`onChangeCategory` werden nur
 * aufgerufen, jede Zustandsänderung (Formular, Hervorhebungs-Timer, Scroll) bleibt in der
 * Sektion (Plan 34-12).
 */

import { memo } from 'react';
import { ActionIcon, Table, Text } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconTrash } from '@tabler/icons-react';

import type {
  AlternativeFuelDerivedEntry,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';
import type { FuelAshCategory } from '../../../services/fuelAshCategoriesApi.js';
import type { WasteCodeUsage } from '../../form/avvCatalog.js';
import { MATRIX_WASTE_CODE_TH } from '../../form/matrixPresets.js';
import { FUEL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import {
  ValidatedAvvSelect,
  ValidatedNumberInput,
  ValidatedTextInput,
} from '../../form/validatedFields.js';
import { AlternativeFuelCategoryMenu } from './AlternativeFuelCategoryMenu.js';
import { formatDerivedCell } from './fuelDerived.js';

type FuelForm = UseFormReturnType<EnvironmentalDataFormData>;

// Feld-Overrides zu FUEL_INPUT_PROPS -- wortgleich mit den Modul-Konstanten aus
// BrennstoffeSection.tsx (dort weiterhin fuer PrimaryFuelRow/OtherFuelRow bis Plan 34-12 die
// Sektion umbaut). Bewusst lokal dupliziert statt von dort importiert: BrennstoffeSection.tsx
// ist derzeit nicht kompilierbar (Plan 34-12 baut sie um), ein Import von dort waere ein
// Rueckwaerts-Abhaengigkeit auf eine Datei, die dieser Plan nicht anfasst. Plan 34-12 zieht
// PrimaryFuelRow/OtherFuelRow auf dieselben Konstanten, sobald die Sektion umgebaut ist -- dann
// gibt es dafuer wieder genau eine Quelle.
const EMISSION_FACTOR_INPUT_PROPS = { ...FUEL_INPUT_PROPS, decimalScale: 6 } as const;
const BIOGENIC_INPUT_PROPS = { ...FUEL_INPUT_PROPS, max: 100 } as const;
// D-04: Aschegehalt in Prozent mit zwei Nachkommastellen. Bewusst OHNE max und OHNE
// Zuruecksetzen-Knopf (Nutzerentscheidung 2026-09-07) -- Mantine wuerde beim Verlassen des
// Feldes still auf eine Obergrenze klemmen, statt den Wert dem Backend zur Ablehnung
// vorzulegen; die einzige Validierungsquelle bleibt das Backend (D-17 aus Phase 33).
const ASH_CONTENT_INPUT_PROPS = { ...FUEL_INPUT_PROPS, decimalScale: 2 } as const;

export interface AlternativeFuelRowProps {
  form: FuelForm;
  /** Position im flachen Formular-Array `alternativeFuels` (siehe Docblock oben). */
  flatIndex: number;
  disabled?: boolean;
  errorSig: string;
  usedCodes: ReadonlyMap<string, WasteCodeUsage>;
  usedCodesSig: string;
  onCodeChange: (field: string, code: string | null) => void;
  onRemove: (flatIndex: number) => void;
  onChangeCategory: (flatIndex: number, categoryId: number) => void;
  /** Die zwölf Sekundär-Kategorien, unverändert an das Kategoriewechsel-Menü durchgereicht. */
  categories: FuelAshCategory[];
  /** Die Kategorie, in deren Gruppe diese Zeile aktuell gerendert wird (D-19: im Menü gesperrt). */
  currentCategoryId: number;
  derived: AlternativeFuelDerivedEntry | undefined;
  calcSig: string;
  /** Hervorhebung nach dem Anlegen oder einem Kategoriewechsel (D-19). Die Zeitsteuerung
   *  (1300 ms) und das Scrollen in den sichtbaren Bereich liegen in der Sektion (Plan 34-12) --
   *  diese Komponente setzt nur die CSS-Klasse. */
  justAdded?: boolean;
}

export const AlternativeFuelRow = memo(
  function AlternativeFuelRow({
    form,
    flatIndex,
    disabled,
    usedCodes,
    onCodeChange,
    onRemove,
    onChangeCategory,
    categories,
    currentCategoryId,
    derived,
    justAdded,
  }: AlternativeFuelRowProps) {
    return (
      <Table.Tr className={justAdded ? 'alternative-fuel-row--just-added' : undefined}>
        <Table.Td>
          <ValidatedTextInput
            form={form}
            field={`alternativeFuels.${flatIndex}.name`}
            placeholder="Bezeichnung"
            disabled={disabled}
          />
        </Table.Td>
        <Table.Td style={MATRIX_WASTE_CODE_TH}>
          <ValidatedAvvSelect
            form={form}
            field={`alternativeFuels.${flatIndex}.wasteCode`}
            disabled={disabled}
            usedCodes={usedCodes}
            onCodeChange={onCodeChange}
          />
          {/*
            B-16-Hinweis ("kein Abfallschluessel angegeben, obwohl eine Menge erfasst ist") wird
            NICHT hier gebaut: er kommt als Feldhinweis vom Backend ueber die bestehende
            Warnungs-Pipeline (SecondaryFuelWasteCodeHint) und zeigt sich bereits ueber
            ValidatedAvvSelect selbst (useFieldMessage liest FieldWarningsContext). Das Backend
            ist die einzige Quelle -- ein zweiter, frontendseitig nachgebauter Hinweis waere eine
            zweite Wahrheit ueber denselben Zustand.

            Der Sketch-Hinweis bei abweichend einsortiertem Schluessel ("Laut Zuordnung: ...")
            ist absichtlich NICHT gebaut: er setzt eine Zuordnung von AVV-Schluesseln zu den
            zwoelf Kategorien voraus, die es im Projekt nicht gibt. `avvCatalog.ts` kennt nur
            AVV-eigene Kapitelgruppen (`groupCode`/`AVV_GROUP_TITLES`), keine Verbindung zu
            `FuelAshCategory`. Das Projektgedaechtnis haelt zur selben Grundsatzfrage fest, dass
            der Schluessel "keine Wirkung" auf die Kategorie hat (Entscheidung 2026-09-07,
            .claude/memory/brennstoffe-abfallschluessel-grundsatzfrage.md) -- eine solche
            Zuordnungstabelle waere selbst eine neue fachliche Klassifikation von 842
            Schluesseln auf zwoelf Kategorien und damit eine eigene Entscheidung, keine reine
            Anzeigelogik. Siehe SUMMARY dieses Plans.
          */}
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`alternativeFuels.${flatIndex}.amount`}
            kind="decimal"
            unit="t"
            align="right"
            disabled={disabled}
            {...FUEL_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`alternativeFuels.${flatIndex}.heatingValue`}
            kind="decimal"
            unit="MJ/kg"
            align="right"
            disabled={disabled}
            {...FUEL_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`alternativeFuels.${flatIndex}.emissionFactor`}
            kind="decimal"
            unit="t/TJ"
            align="right"
            disabled={disabled}
            {...EMISSION_FACTOR_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`alternativeFuels.${flatIndex}.biogenicContent`}
            kind="decimal"
            unit="%"
            align="right"
            disabled={disabled}
            {...BIOGENIC_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          <ValidatedNumberInput
            form={form}
            field={`alternativeFuels.${flatIndex}.ashContent`}
            kind="decimal"
            unit="%"
            align="right"
            disabled={disabled}
            {...ASH_CONTENT_INPUT_PROPS}
          />
        </Table.Td>
        <Table.Td>
          {/* Anzeigezelle statt schreibgeschuetztem NumberInput -- serverseitig berechnet, nie
              aus form gelesen oder dorthin zurueckgeschrieben (gleiches Muster wie bei den
              Primaerbrennstoffen und den entfallenen "Sonstige Brennstoffe"). */}
          <Text size="sm" ta="right">{formatDerivedCell(derived?.thermalEnergy)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ta="right">{formatDerivedCell(derived?.fuelAsh)}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ta="right">{formatDerivedCell(derived?.biogenicCo2)}</Text>
        </Table.Td>
        <Table.Td>
          <ActionIcon.Group>
            <AlternativeFuelCategoryMenu
              categories={categories}
              currentCategoryId={currentCategoryId}
              disabled={disabled}
              onSelect={(categoryId) => onChangeCategory(flatIndex, categoryId)}
            />
            <ActionIcon
              color="red"
              variant="subtle"
              onClick={() => onRemove(flatIndex)}
              disabled={disabled}
              aria-label="Brennstoffzeile entfernen"
              title="Brennstoffzeile entfernen"
            >
              <IconTrash size={16} />
            </ActionIcon>
          </ActionIcon.Group>
        </Table.Td>
      </Table.Tr>
    );
  },
  // usedCodesSig (Pitfall 2), calcSig (T-30-13): siehe Kommentare an den bestehenden
  // Zeilenkomponenten in BrennstoffeSection.tsx -- gleiche Begruendung, hier uebernommen.
  // currentCategoryId zusaetzlich: ein Kategoriewechsel aendert weder errorSig noch flatIndex
  // noch usedCodesSig/calcSig -- ohne diesen Vergleich wuerde das Kategoriewechsel-Menue nach
  // dem Wechsel weiterhin die ALTE Kategorie als gesperrt zeigen.
  (prev, next) =>
    prev.errorSig === next.errorSig &&
    prev.disabled === next.disabled &&
    prev.flatIndex === next.flatIndex &&
    prev.usedCodesSig === next.usedCodesSig &&
    prev.calcSig === next.calcSig &&
    prev.currentCategoryId === next.currentCategoryId &&
    prev.categories === next.categories &&
    prev.justAdded === next.justAdded,
);
